import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { registerModalBackHandler } from '../../lib/native';
import { formatMoney } from '../../lib/hostInvoice';
import { formatIban } from '../../lib/billing';
import { reviewChecks, type ReviewBilling } from '../../lib/invoiceReview';
import { notifyInvoiceEvent } from '../../lib/invoiceNotify';
import { Button } from '../design-system';
import type { AdminHostInvoice, HostBillingDetails } from '../../types';

interface TripLine {
  matchId: string;
  title: string;
  dates: string;
  amountCents: number;
  releasedAt: string | null;
}

interface InvoiceReviewModalProps {
  invoice: AdminHostInvoice;
  /** The signed-in admin (an admin can't approve their own invoice). */
  adminId: string;
  onClose: () => void;
  /** Called after an approve or reject, so the list can refresh. */
  onChanged: () => void;
}

const ICON = { ok: 'ti-circle-check text-emerald-500', warn: 'ti-alert-triangle text-gold', bad: 'ti-circle-x text-crimson' } as const;

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-app-muted">{label}</span>
      <span className="text-app-fg text-right break-words min-w-0">{value}</span>
    </div>
  );
}

const day = (iso: string) => new Date(iso).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Everything an admin needs to decide on a host invoice: the recibo itself, its
 * numbers, the trips it covers, the account it would be paid to, and a checklist
 * of the things only a person can judge. Approving copies the host's current
 * name and IBAN onto the invoice; rejecting needs a reason and frees the trips.
 */
export function InvoiceReviewModal({ invoice: inv, adminId, onClose, onChanged }: InvoiceReviewModalProps) {
  const [trips, setTrips] = useState<TripLine[]>([]);
  const [billing, setBilling] = useState<HostBillingDetails | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => registerModalBackHandler(() => { if (!busy) onClose(); }), [onClose, busy]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [{ data: items }, { data: bill }] = await Promise.all([
        supabase.from('host_invoice_items').select('match_id, amount_cents, released_at').eq('invoice_id', inv.id),
        supabase.from('host_billing_details').select('*').eq('host_id', inv.host_id).maybeSingle(),
      ]);
      const rows = (items ?? []) as { match_id: string | null; amount_cents: number; released_at: string | null }[];
      const matchIds = rows.map((r) => r.match_id).filter(Boolean) as string[];
      const { data: matches } = matchIds.length
        ? await supabase.from('matches').select('id, portrait_id').in('id', matchIds)
        : { data: [] };
      const portraitIds = ((matches ?? []) as { portrait_id: string | null }[]).map((m) => m.portrait_id).filter(Boolean) as string[];
      const { data: portraits } = portraitIds.length
        ? await supabase.from('taste_portraits').select('id, trip_name, start_date, trip_days').in('id', portraitIds)
        : { data: [] };
      type P = { id: string; trip_name: string | null; start_date: string | null; trip_days: number | null };
      const portraitById = new Map(((portraits ?? []) as P[]).map((p) => [p.id, p]));
      const portraitOf = new Map(((matches ?? []) as { id: string; portrait_id: string | null }[]).map((m) => [m.id, m.portrait_id ? portraitById.get(m.portrait_id) : undefined]));
      if (cancelled) return;
      setTrips(rows.map((r) => {
        const p = r.match_id ? portraitOf.get(r.match_id) : undefined;
        const dates = p?.start_date
          ? new Date(p.start_date).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' }) + (p.trip_days ? ` · ${p.trip_days} days` : '')
          : '';
        return { matchId: r.match_id ?? '', title: p?.trip_name ?? 'Trip', dates, amountCents: r.amount_cents, releasedAt: r.released_at };
      }));
      setBilling((bill as HostBillingDetails | null) ?? null);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [inv.id, inv.host_id]);

  const checks = useMemo(
    () => reviewChecks(
      inv,
      trips.map((t) => ({ amount_cents: t.amountCents, released_at: t.releasedAt })),
      billing as ReviewBilling | null,
      adminId,
      new Date(),
    ),
    [inv, trips, billing, adminId],
  );
  const blocked = !loaded || checks.some((c) => c.tone === 'bad');
  const liveTrips = trips.filter((t) => t.releasedAt === null);
  const hostName = inv.profiles?.full_name ?? 'Host';
  const canDecide = inv.status === 'submitted';
  const canReject = inv.status === 'submitted' || inv.status === 'approved';

  async function openFile(bucket: string, path: string) {
    const { data, error: e } = await supabase.storage.from(bucket).createSignedUrl(path, 60);
    if (e || !data?.signedUrl) { setError('We couldn’t open that file. Please try again.'); return; }
    setError(null);
    window.open(data.signedUrl, '_blank', 'noopener');
  }

  async function openProof() {
    const { data } = await supabase.from('payout_runs').select('proof_file_path').eq('id', inv.payout_run_id).maybeSingle();
    const path = (data as { proof_file_path: string } | null)?.proof_file_path;
    if (!path) { setError('There is no proof of payment on record for this invoice.'); return; }
    await openFile('payout-proofs', path);
  }

  async function approve() {
    setBusy(true);
    setError(null);
    const { error: e } = await supabase.rpc('approve_host_invoice', { p_invoice_id: inv.id });
    setBusy(false);
    if (e) { setError(e.message); return; }
    void notifyInvoiceEvent(inv.id, 'approved');
    onChanged();
  }

  async function reject() {
    if (reason.trim().length < 3) { setError('Tell the host why, in a few words.'); return; }
    setBusy(true);
    setError(null);
    const { error: e } = await supabase.rpc('reject_host_invoice', { p_invoice_id: inv.id, p_reason: reason.trim() });
    setBusy(false);
    if (e) { setError(e.message); return; }
    void notifyInvoiceEvent(inv.id, 'rejected');
    onChanged();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60" onClick={() => { if (!busy) onClose(); }}>
      <div
        role="dialog"
        aria-label={`Invoice ${inv.invoice_number} from ${hostName}`}
        className="bg-app-bg md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-2xl max-h-dvh-92 overflow-y-auto pb-safe-or-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 md:p-6 flex flex-col gap-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-app-fg truncate">{hostName} · {inv.invoice_number}</h2>
              <p className="text-xs text-app-muted mt-0.5">{inv.number} · sent {day(inv.submitted_at)} · <span className="capitalize">{inv.status}</span></p>
            </div>
            <button type="button" aria-label="Close" onClick={onClose} disabled={busy} className="text-app-muted text-xl leading-none">×</button>
          </div>

          <section className="rounded-2xl border border-app-border bg-app-card p-4 flex flex-col gap-1.5">
            <Fact label="Type" value={inv.invoice_kind === 'recibo_verde' ? 'Recibo verde' : 'Fatura'} />
            <Fact label="Invoice date" value={day(inv.invoice_date)} />
            <Fact label="Total" value={formatMoney(inv.total_cents, inv.currency)} />
            <Fact label="Base amount" value={formatMoney(inv.invoice_base_cents, inv.currency)} />
            <Fact label="VAT" value={inv.invoice_vat_cents > 0 ? formatMoney(inv.invoice_vat_cents, inv.currency) : `None (${inv.invoice_vat_exemption ?? ''})`} />
            {inv.invoice_withholding_cents > 0 && <Fact label="Tax withheld" value={`−${formatMoney(inv.invoice_withholding_cents, inv.currency)}`} />}
            <Fact label="We pay" value={formatMoney(inv.invoice_payable_cents, inv.currency)} />
            {inv.status === 'paid' && <Fact label="Paid" value={`${inv.paid_at ? day(inv.paid_at) : ''} · ref. ${inv.payment_reference ?? ''}`} />}
            {inv.status === 'rejected' && <Fact label="Sent back" value={inv.rejected_reason ?? ''} />}
          </section>

          <section className="flex flex-col gap-1.5" aria-label="Trips on this invoice">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-app-muted">Trips</h3>
            {!loaded ? <p className="text-sm text-app-muted">Loading…</p> : liveTrips.length === 0 ? (
              <p className="text-sm text-app-muted">No trips are attached to this invoice any more.</p>
            ) : liveTrips.map((t, i) => (
              <div key={`${t.matchId}-${i}`} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 truncate text-app-fg">{t.title}<span className="text-app-muted">{t.dates ? ` · ${t.dates}` : ''}</span></span>
                <span className="shrink-0 text-app-fg">{formatMoney(t.amountCents, inv.currency)}</span>
              </div>
            ))}
          </section>

          {loaded && (
            <section className="flex flex-col gap-1.5" aria-label="Host account">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-app-muted">
                {inv.payout_iban ? 'Approved to be paid to' : 'Host account'}
              </h3>
              {inv.payout_iban ? (
                <p className="text-sm text-app-fg">{inv.payout_account_name} · {formatIban(inv.payout_iban)}</p>
              ) : billing ? (
                <>
                  <p className="text-sm text-app-fg">{billing.legal_name} · NIF {billing.nif}</p>
                  <p className="text-xs text-app-muted">{billing.address_line}, {billing.postal_code} {billing.city}</p>
                  <p className="text-sm text-app-fg">{formatIban(billing.iban)}</p>
                </>
              ) : <p className="text-sm text-crimson">No billing details on file.</p>}
            </section>
          )}

          {canDecide && loaded && (
            <section className="flex flex-col gap-2" aria-label="Checklist">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-app-muted">Before you approve</h3>
              <ul className="flex flex-col gap-1.5">
                {checks.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-app-fg">
                    <i className={`ti ${ICON[c.tone]} mt-0.5 shrink-0`} aria-hidden="true" />
                    <span>{c.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {rejecting && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="reject-reason" className="text-[13px] font-medium text-app-fg-2">Why is it being sent back? The host will see this.</label>
              <textarea id="reject-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy}
                className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg" />
            </div>
          )}

          {error ? <p role="alert" className="text-sm text-crimson">{error}</p> : null}

          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => void openFile('host-invoices', inv.invoice_file_path)} disabled={busy}>View recibo</Button>
            {inv.status === 'paid' && inv.payout_run_id && (
              <Button variant="secondary" onClick={() => void openProof()} disabled={busy}>View proof of payment</Button>
            )}
            {canDecide && !rejecting && <Button onClick={() => void approve()} disabled={busy || blocked}>{busy ? 'Working…' : 'Approve'}</Button>}
            {canReject && !rejecting && <Button variant="outline" onClick={() => { setRejecting(true); setError(null); }} disabled={busy}>Send back</Button>}
            {rejecting && <Button onClick={() => void reject()} disabled={busy}>{busy ? 'Working…' : 'Send back to the host'}</Button>}
            {rejecting && <Button variant="secondary" onClick={() => { setRejecting(false); setError(null); }} disabled={busy}>Cancel</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvoiceReviewModal;
